/**
 * Static registry of navigable destinations for the top-nav search "Go to"
 * section. Data only — pure module, no runtime deps, tree-shakeable,
 * unit-testable.
 *
 * Each entry pairs a canonical English field with a `OtherLanguages`
 * translations array:
 *   - `titleEnglish` + `titleOtherLanguages`
 *   - `keywordsEnglish` + `keywordsOtherLanguages`
 *
 * `titleEnglish` doubles as a flat i18n key — the render layer wraps it in
 * `useTranslation().t()` so the user sees their locale's label. `crumbs`
 * work the same way (each string is a flat i18n key).
 *
 * The `*OtherLanguages` arrays hardcode every supported-locale translation
 * (mirrors the backend pattern in `search.service.ts` → DEFAULT_TEAM_NAMES).
 * The matcher scores the query against every string in the English +
 * OtherLanguages union — so a French user typing "écran" finds Screens even
 * in an English UI, and even before Lokalise ships a new translation.
 * Update in sync with the JSON files if a label changes.
 *
 * Visibility gates live in `NavGate` (feature/role/branding hides). The pure
 * `matchNavTargets` matcher does NOT read runtime state — the consumer
 * filters the registry with `useVisibleNavTargets()` first, then passes the
 * already-filtered list to the matcher.
 */

/**
 * Runtime-state gate applied by `useVisibleNavTargets`. All fields optional;
 * a target with no gate is always visible. Multiple gates AND together.
 */
export interface NavGate {
  /** Company feature flag required — key matches `useCompanyFeatures()` output.
   * Special case: `'mdm'` reads `company.feature.mdm` directly (not surfaced by the hook).
   *
   * v2 additions used by action entries:
   *   - `advancedDeviceManagement` — Pro Plus+ (Screens Preconfigure Devices)
   *   - `remoteCommand` — plan tier (Screens Execute Remote Commands)
   *   - `customBrandCanva` — Templates canvas feature (Assets Choose Template);
   *     `useCompanyFeatures` exposes this from `company.feature.customBrandCanva`.
   *     Note the spelling drift with `brandingData.customBrandCanvas` (with an
   *     `s`) that AssetsPage also reads — same intent, two source fields.
   *   - `roleSecurity` — Pro Plus+ custom roles (Account Members Manage Roles) */
  feature?:
    | 'samlSSO'
    | 'securityAuditLogging'
    | 'alertPolicy'
    | 'branding'
    | 'externalCom'
    | 'proofOfPlay'
    | 'shareLink'
    | 'allowTeams'
    | 'feedsCreate'
    | 'mdm'
    | 'advancedDeviceManagement'
    | 'remoteCommand'
    | 'customBrandCanva'
    | 'roleSecurity';
  /** Minimum role — hidden below this level.
   *   'admin'      = any team admin (matches `isTeamAdmin` — includes TEAM_OWNER)
   *   'rootAdmin'  = root-level admin (matches `isRootAdmin` — rootRole ∈
   *                    OWNER/SUPER_ADMIN/ADMIN, EXCLUDES TEAM_OWNER)
   *   'superAdmin' = root SUPER_ADMIN+ (matches `isRootSuperAdmin`)
   *   'owner'      = root OWNER only (matches `isRootOwner`) */
  minRole?: 'admin' | 'rootAdmin' | 'superAdmin' | 'owner';
  /** Hide when `brandingData.isWhiteLabel` — OptiSigns-program surfaces. */
  whiteLabelHidden?: boolean;
  /** Hide when `brandingData.removeOptiSignsBranding` — OptiSigns-attributed features. */
  brandingRemovedHidden?: boolean;
  /** Per-page hide flag on brandingData (Branding Settings toggles). */
  brandingFlag?:
    | 'hideProofofPlay'
    | 'hideSubscriptionPages'
    | 'hidePaymentHistoryPages'
    | 'hideAuditLogging'
    | 'hideMonitorAndAlert'
    | 'hideAccountMembersPage'
    | 'hideExternalCommunications';
  /** Hide on the OptiEdge variant (signage-only pages). */
  signageOnly?: boolean;
  /** Hide for READ_ONLY / CONTENT_PROPOSAL / CONTENT_APPROVAL roles —
   *  mirrors `RestrictedRoute` (routes/index.tsx) which redirects those
   *  three roles away from the destination.
   *
   *  Design principle: role-based hides are strict — a user who can't
   *  ACCESS the page should not SEE it in search either. (Feature/plan
   *  mismatches, in contrast, are OK to show — the destination surfaces
   *  an "Upgrade Now" pitch which is a discovery win.) */
  restrictedRolesHidden?: boolean;
  /** Hide when the account is a CHILD sub-account. Detected in
   *  `SettingsLayout` as `!!company.parentAccountId || !!company.linkedAccountId`.
   *  Used by Sub-Account Billing. */
  childAccountHidden?: boolean;
  /** Hide when the active team is not the default team. Detected via
   *  `useTeamStore().isDefaultTeam`. Used by Order Devices, Order History,
   *  and Coupons (page code gates behind `isRootAdminDefaultTeam` in
   *  `SettingsLayout`). */
  defaultTeamOnly?: boolean;
  /** Hide when the active team IS the default team. Inverse of
   *  `defaultTeamOnly`. Used by Update Team — a per-team modal that only
   *  exists in each non-default team's ⋮ dropdown; Default Team has no
   *  Update Team analog (root company edits go through Company Settings). */
  nonDefaultTeamOnly?: boolean;
  /** Hide when `usePermissions.canCreate(entity)` returns false. Used by v2
   *  action entries that mirror the destination toolbar's create-write gate —
   *  precisely tracks which roles can perform the action:
   *
   *   - `asset`    — all roles except READ_ONLY (CONTENT_PROPOSAL allowed)
   *   - `playlist` — all roles except READ_ONLY + CONTENT_PROPOSAL
   *   - `schedule` — same as playlist
   *   - `screen`   — same as playlist
   *
   *  Role-only check (no folder context) — folder-security disables are
   *  runtime UX and stay in-page, not modelled in NavGate. */
  canCreate?: 'asset' | 'playlist' | 'schedule' | 'screen';
}

export interface NavTarget {
  id: string;
  /** English title. Doubles as a flat i18n key — render layer wraps in `t()`. */
  titleEnglish: string;
  /**
   * Every supported-locale translation of the title. The matcher scores the
   * query against each string in this array, so cross-language search works
   * in any UI regardless of Lokalise translation state. Omit only for
   * entries where the label is identical across every locale (technical
   * acronyms like `Wi-Fi & 802.1X`).
   */
  titleOtherLanguages?: string[];
  crumbs: string[];
  route: string;
  /**
   * Slug of an action to fire after navigation lands on `route`. Nullable —
   * pure-nav entries omit it. `nav-actions.ts` maps the slug to a store call
   * (or a `?action=` URL param the destination page reads on mount when the
   * trigger is page-local, e.g. Transloadit upload).
   *
   * Feature-agnostic string keeps this registry a pure data file — the
   * cross-feature imports live only in the dispatcher.
   */
  openDialog?: string;
  /** Canonical English match tokens. */
  keywordsEnglish?: string[];
  /**
   * Non-English translations of `keywordsEnglish`, flattened across all
   * supported locales. Scored the same way as `keywordsEnglish` — a French
   * user typing `"appareil"` finds Screens because `"appareil"` sits in
   * this array regardless of UI language. English stays in `keywordsEnglish`
   * so the canonical set is grep-able. Omit for entries where every keyword
   * is an acronym / product name (SAML, TV, MDM, Facebook, PowerBI).
   */
  keywordsOtherLanguages?: string[];
  /**
   * Render this action with its full crumb path even when nothing else in the
   * result set shares its title.
   *
   * For actions whose title is ambiguous IN THE PRODUCT, not just within a
   * given list — "Add App" exists on both Files/Assets and Engage, and each
   * opens a different picker. The runtime collision check below only fires when
   * both happen to match the same query, so searching "engage" surfaced one
   * bare "Add App" row with nothing saying which of the two it was.
   */
  alwaysQualify?: boolean;
  gate?: NavGate;
}

export const NAV_TARGETS: NavTarget[] = [
  // Content — top-level nav (signage-only)
  {
    id: 'nav.screens',
    titleEnglish: 'Screens',
    titleOtherLanguages: [
      'Bildschirme',
      'Pantallas',
      'Écrans',
      'Schermate',
      'スクリーン',
      'Schermen',
      'Telas',
      'Skärmar',
    ],
    crumbs: ['Screens'],
    route: '/app/screenManagement',
    keywordsEnglish: ['device', 'devices', 'display', 'monitor', 'tv'],
    keywordsOtherLanguages: [
      'gerät',
      'dispositivo',
      'appareil',
      'デバイス',
      'apparaat',
      'enhet',
      'geräte',
      'dispositivos',
      'appareils',
      'dispositivi',
      'apparaten',
      'enheter',
      'anzeige',
      'pantalla',
      'affichage',
      'schermo',
      'ディスプレイ',
      'weergave',
      'exibição',
      'moniteur',
      'モニター',
      'skärm',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.assets',
    titleEnglish: 'Assets',
    titleOtherLanguages: [
      'Vermögenswerte',
      'Activos',
      'Actifs',
      'Attività',
      '資産',
      'Activa',
      'Ativos',
      'Tillgångar',
    ],
    crumbs: ['Assets'],
    route: '/app/assetManagement',
    keywordsEnglish: ['file', 'files', 'media', 'image', 'video', 'upload', 'content'],
    keywordsOtherLanguages: [
      'datei',
      'archivo',
      'fichier',
      'ファイル',
      'bestand',
      'arquivo',
      'fil',
      'dateien',
      'archivos',
      'fichiers',
      'bestanden',
      'arquivos',
      'filer',
      'medien',
      'medios',
      'média',
      'メディア',
      'mídia',
      'bild',
      'imagen',
      'immagine',
      '画像',
      'afbeelding',
      'imagem',
      'vidéo',
      'ビデオ',
      'hochladen',
      'cargar',
      'téléverser',
      'caricare',
      'アップロード',
      'uploaden',
      'carregar',
      'ladda upp',
      'inhalt',
      'contenido',
      'contenu',
      'contenuto',
      'コンテンツ',
      'inhoud',
      'conteúdo',
      'innehåll',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.playlists',
    titleEnglish: 'Playlists',
    titleOtherLanguages: [
      'Wiedergabelisten',
      'Listas de reproducción',
      'Listes de lecture',
      'Playlist',
      'プレイリスト',
      'Afspeellijsten',
      'Listas de reprodução',
      'Spellistor',
    ],
    crumbs: ['Playlists'],
    route: '/app/playlistManagement',
    keywordsEnglish: ['playlist', 'rotation', 'sequence'],
    keywordsOtherLanguages: [
      'rotación',
      'rotazione',
      'ローテーション',
      'rotatie',
      'rotação',
      'sequenz',
      'secuencia',
      'séquence',
      'sequenza',
      'シーケンス',
      'reeks',
      'sequência',
      'sekvens',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.schedules',
    titleEnglish: 'Schedules',
    titleOtherLanguages: [
      'Zeitpläne',
      'Horarios',
      'Horaires',
      'Orari',
      'スケジュール',
      'Roosters',
      'Horários',
      'Scheman',
    ],
    crumbs: ['Schedules'],
    route: '/app/scheduleManagement',
    keywordsEnglish: ['schedule', 'calendar', 'dayparting', 'recurring'],
    keywordsOtherLanguages: [
      'zeitplan',
      'horario',
      'horaire',
      'orario',
      'スケジュール',
      'schema',
      'horário',
      'kalender',
      'calendario',
      'calendrier',
      'カレンダー',
      'calendário',
      'wiederkehrend',
      'recurrente',
      'récurrent',
      'ricorrente',
      '定期',
      'terugkerend',
      'recorrente',
      'återkommande',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.templates',
    titleEnglish: 'Templates',
    titleOtherLanguages: [
      'Vorlagen',
      'Plantillas',
      'Modèles',
      'Modelli',
      'テンプレート',
      'Sjablonen',
      'Modelos',
      'Mallar',
    ],
    crumbs: ['Templates'],
    route: '/app/templateManagement',
    keywordsEnglish: ['template', 'design', 'choose', 'canvas', 'pre-built', 'gallery'],
    keywordsOtherLanguages: [
      'vorlage',
      'plantilla',
      'modèle',
      'modello',
      'テンプレート',
      'sjabloon',
      'modelo',
      'mall',
      'design',
      'diseño',
      'デザイン',
      'ontwerp',
      'auswählen',
      'elegir',
      'choisir',
      'scegliere',
      '選択',
      'kiezen',
      'escolher',
      'välj',
      'galerie',
      'galería',
      'galleria',
      'ギャラリー',
      'galerij',
      'galeria',
      'galleri',
    ],
    gate: { signageOnly: true, restrictedRolesHidden: true },
  },
  {
    id: 'nav.engage',
    titleEnglish: 'Engage',
    titleOtherLanguages: [
      'Engagieren',
      'Comprometer',
      "S'engager",
      'Ingaggiare',
      '従事する',
      'Erbij betrekken',
      'Envolver',
      'Engagera',
    ],
    crumbs: ['Engage'],
    route: '/app/engageManagement',
    keywordsEnglish: ['engage', 'app', 'apps'],
    keywordsOtherLanguages: [
      'engagieren',
      'comprometer',
      "s'engager",
      'ingaggiare',
      '従事する',
      'erbij betrekken',
      'envolver',
      'engagera',
      'app',
      'aplicación',
      'application',
      'アプリ',
      'aplicativo',
      'anwendung',
      'applicazione',
      'toepassing',
      'applikation',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.engage-hub',
    titleEnglish: 'Engage Hub',
    titleOtherLanguages: [
      'Engage-Hub',
      'Centro Engage',
      'Hub Engage',
      'Hub Engage',
      'エンゲージハブ',
      'Engage-hub',
      'Hub Engage',
      'Engage-hub',
    ],
    crumbs: ['Engage', 'Engage Hub'],
    route: '/app/crm-management',
    keywordsEnglish: ['hub', 'crm', 'engagement', 'customers', 'contacts', 'leads'],
    keywordsOtherLanguages: [
      'engagieren',
      'comprometer',
      "s'engager",
      'ingaggiare',
      '従事する',
      'erbij betrekken',
      'envolver',
      'engagera',
      'app',
      'aplicación',
      'application',
      'アプリ',
      'aplicativo',
      'anwendung',
      'applicazione',
      'toepassing',
      'applikation',
    ],
    gate: { signageOnly: true },
  },
  {
    id: 'nav.analytics',
    titleEnglish: 'Analytics',
    titleOtherLanguages: [
      'Analysen',
      'Analítica',
      'Analytique',
      'Analisi',
      '分析',
      'Analyses',
      'Análises',
      'Analys',
    ],
    crumbs: ['Analytics'],
    route: '/app/ai-analytics',
    keywordsEnglish: ['analytics', 'audience', 'ai', 'insights', 'stats'],
    keywordsOtherLanguages: [
      'publikum',
      'audiencia',
      'pubblico',
      'オーディエンス',
      'publiek',
      'audiência',
      'publik',
      'einblicke',
      'información',
      'approfondimenti',
      'インサイト',
      'inzichten',
      'insikter',
      'statistiken',
      'estadísticas',
      'statistiques',
      'statistiche',
      '統計',
      'statistieken',
      'estatísticas',
      'statistik',
    ],
    gate: { signageOnly: true, minRole: 'rootAdmin' },
  },

  // MDM — feature-gated, OptiSigns-attributed
  {
    id: 'nav.mdm.devices',
    titleEnglish: 'Devices',
    titleOtherLanguages: [
      'Geräte',
      'Dispositivos',
      'Appareils',
      'Dispositivi',
      'デバイス',
      'Apparaten',
      'Dispositivos',
      'Enheter',
    ],
    crumbs: ['MDM', 'Devices'],
    route: '/app/mdm',
    keywordsEnglish: ['mdm', 'device', 'devices', 'fleet', 'manage'],
    keywordsOtherLanguages: [
      'gerät',
      'dispositivo',
      'appareil',
      'デバイス',
      'apparaat',
      'enhet',
      'geräte',
      'dispositivos',
      'appareils',
      'dispositivi',
      'apparaten',
      'enheter',
      'flotte',
      'flota',
      'flotta',
      'フリート',
      'vloot',
      'frota',
      'verwalten',
      'gestionar',
      'gérer',
      'gestire',
      '管理',
      'beheren',
      'gerenciar',
      'hantera',
    ],
    gate: { feature: 'mdm', brandingRemovedHidden: true, minRole: 'superAdmin' },
  },
  {
    id: 'nav.mdm.fleets',
    titleEnglish: 'Fleets',
    titleOtherLanguages: [
      'Flotten',
      'Flotas',
      'Flottes',
      'Flotte',
      'フリート',
      'Vloten',
      'Frotas',
      'Flottor',
    ],
    crumbs: ['MDM', 'Fleets'],
    route: '/app/mdm/fleets',
    keywordsEnglish: ['mdm', 'fleet', 'fleets', 'group', 'batch'],
    keywordsOtherLanguages: [
      'flotte',
      'flota',
      'flotta',
      'フリート',
      'vloot',
      'frota',
      'flotten',
      'flotas',
      'flottes',
      'vloten',
      'frotas',
      'flottor',
      'gruppe',
      'grupo',
      'groupe',
      'gruppo',
      'グループ',
      'groep',
      'grupp',
      'stapel',
      'lote',
      'lot',
      'lotto',
      'バッチ',
    ],
    gate: { feature: 'mdm', brandingRemovedHidden: true, minRole: 'superAdmin' },
  },
  {
    id: 'nav.mdm.provisioning-templates',
    titleEnglish: 'Provisioning Templates',
    titleOtherLanguages: [
      'Bereitstellungsvorlagen',
      'Plantillas de aprovisionamiento',
      'Modèles de provisionnement',
      'Modelli di provisioning',
      'プロビジョニングテンプレート',
      'Provisioneringssjablonen',
      'Modelos de provisionamento',
      'Provisioneringsmallar',
    ],
    crumbs: ['MDM', 'Provisioning Templates'],
    route: '/app/mdm/templates',
    keywordsEnglish: ['provision', 'provisioning', 'template', 'mdm', 'onboarding'],
    keywordsOtherLanguages: [
      'bereitstellen',
      'aprovisionar',
      'provisionner',
      'provisioning',
      'プロビジョニング',
      'provisioneren',
      'provisionar',
      'provisionera',
      'bereitstellung',
      'aprovisionamiento',
      'provisionnement',
      'provisionamento',
      'vorlage',
      'plantilla',
      'modèle',
      'modello',
      'テンプレート',
      'sjabloon',
      'modelo',
      'mall',
    ],
    gate: { feature: 'mdm', brandingRemovedHidden: true, minRole: 'superAdmin' },
  },
  {
    id: 'nav.mdm.wifi-profiles',
    titleEnglish: 'Wi-Fi & 802.1X',
    crumbs: ['MDM', 'Wi-Fi & 802.1X'],
    route: '/app/mdm/wifi-profiles',
    keywordsEnglish: ['wifi', 'wi-fi', '802.1x', 'enterprise', 'network', 'radius'],
    keywordsOtherLanguages: [
      'unternehmen',
      'empresa',
      'entreprise',
      'imprese',
      '企業',
      'onderneming',
      'företag',
      'netzwerk',
      'red',
      'réseau',
      'rete',
      'ネットワーク',
      'netwerk',
      'rede',
      'nätverk',
      '企業',
      'företag',
      'wlan',
      'wifi',
      'ワイファイ',
    ],
    gate: { feature: 'mdm', brandingRemovedHidden: true, minRole: 'superAdmin' },
  },

  // Orders
  {
    id: 'nav.order-devices',
    titleEnglish: 'Order Devices',
    titleOtherLanguages: [
      'Geräte bestellen',
      'Pedido de dispositivos',
      'Appareils de commande',
      'Ordina i dispositivi',
      'デバイスの注文',
      'Apparaten bestellen',
      'Encomendar dispositivos',
      'Beställ enheter',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Order Devices'],
    route: '/app/s/order-device',
    keywordsEnglish: ['order', 'buy', 'purchase', 'device', 'hardware', 'optistick'],
    keywordsOtherLanguages: [
      'bestellung',
      'pedido',
      'commande',
      'ordine',
      '注文',
      'bestelling',
      'beställning',
      'kaufen',
      'comprar',
      'acheter',
      'comprare',
      '購入',
      'kopen',
      'köpa',
      'einkauf',
      'compra',
      'achat',
      'acquisto',
      'aankoop',
      'inköp',
      'gerät',
      'dispositivo',
      'appareil',
      'デバイス',
      'apparaat',
      'enhet',
    ],
    gate: { whiteLabelHidden: true, minRole: 'rootAdmin', defaultTeamOnly: true },
  },
  {
    id: 'nav.order-history',
    titleEnglish: 'Order History',
    titleOtherLanguages: [
      'Bestellhistorie',
      'Historial de pedidos',
      'Historique des commandes',
      'Cronologia degli ordini',
      '注文履歴',
      'Bestelgeschiedenis',
      'Histórico de pedidos',
      'Orderhistorik',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Order History'],
    route: '/app/s/ordered-device',
    keywordsEnglish: ['order', 'history', 'purchase', 'shipment'],
    keywordsOtherLanguages: [
      'bestellung',
      'pedido',
      'commande',
      'ordine',
      '注文',
      'bestelling',
      'beställning',
      'verlauf',
      'historial',
      'historique',
      'cronologia',
      '履歴',
      'geschiedenis',
      'histórico',
      'historik',
      'einkauf',
      'compra',
      'achat',
      'acquisto',
      '購入',
      'aankoop',
      'inköp',
      'versand',
      'envío',
      'expédition',
      'spedizione',
      '出荷',
      'verzending',
      'envio',
      'leverans',
    ],
    gate: { whiteLabelHidden: true, minRole: 'rootAdmin', defaultTeamOnly: true },
  },

  // Settings — Account
  {
    id: 'settings.personal-profile',
    titleEnglish: 'Personal Profile',
    titleOtherLanguages: [
      'Persönliches Profil',
      'Perfil personal',
      'Profil personnel',
      'Profilo personale',
      '個人プロフィール',
      'Persoonlijk profiel',
      'Perfil pessoal',
      'Personlig profil',
    ],
    crumbs: ['Settings', 'Account', 'Personal Profile'],
    route: '/app/s/personal-profile',
    keywordsEnglish: [
      'profile',
      'name',
      'email',
      'password',
      'photo',
      'avatar',
      'language',
      'timezone',
    ],
    keywordsOtherLanguages: [
      'profil',
      'perfil',
      'profilo',
      'プロフィール',
      'profiel',
      'nombre',
      'nom',
      'nome',
      '名前',
      'naam',
      'namn',
      'passwort',
      'contraseña',
      'mot de passe',
      'パスワード',
      'wachtwoord',
      'senha',
      'lösenord',
      '写真',
      'sprache',
      'idioma',
      'langue',
      'lingua',
      '言語',
      'taal',
      'språk',
    ],
  },
  {
    id: 'settings.company-profile',
    titleEnglish: 'Company Settings',
    titleOtherLanguages: [
      'Unternehmenseinstellungen',
      'Configuración de la empresa',
      "Paramètres de l'entreprise",
      'Impostazioni aziendali',
      '会社設定',
      'Bedrijfsinstellingen',
      'Configurações da empresa',
      'Företagsinställningar',
    ],
    crumbs: ['Settings', 'Account', 'Company Settings'],
    route: '/app/s/company-profile',
    keywordsEnglish: ['company', 'organization', 'account', 'logo', 'address'],
    keywordsOtherLanguages: [
      'firma',
      'empresa',
      'entreprise',
      'azienda',
      '会社',
      'bedrijf',
      'företag',
      'organisation',
      'organización',
      'organizzazione',
      '組織',
      'organisatie',
      'organização',
      'konto',
      'cuenta',
      'compte',
      'conto',
      'アカウント',
      'conta',
      'adresse',
      'dirección',
      'indirizzo',
      '住所',
      'adres',
      'endereço',
      'adress',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },
  {
    id: 'settings.account-members',
    titleEnglish: 'Account Members',
    titleOtherLanguages: [
      'Kontomitglieder',
      'Miembros de la cuenta',
      'Membres du compte',
      "Membri dell'account",
      'アカウントメンバー',
      'Accountleden',
      'Membros da conta',
      'Kontomedlemmar',
    ],
    crumbs: ['Settings', 'Account', 'Account Members'],
    route: '/app/s/account-members',
    keywordsEnglish: [
      'member',
      'members',
      'users',
      'invite',
      'team',
      'role',
      'permission',
      'access',
    ],
    keywordsOtherLanguages: [
      'mitglied',
      'miembro',
      'membre',
      'membro',
      'メンバー',
      'lid',
      'medlem',
      'mitglieder',
      'miembros',
      'membres',
      'membri',
      'leden',
      'membros',
      'medlemmar',
      'benutzer',
      'usuarios',
      'utilisateurs',
      'utenti',
      'ユーザー',
      'gebruikers',
      'usuários',
      'användare',
      'einladen',
      'invitar',
      'inviter',
      'invitare',
      '招待',
      'uitnodigen',
      'convidar',
      'bjuda in',
      'equipo',
      'équipe',
      'squadra',
      'チーム',
      'equipe',
      'rolle',
      'rol',
      'rôle',
      'ruolo',
      '役割',
      'função',
      'roll',
      'berechtigung',
      'permiso',
      'autorisation',
      'permesso',
      '権限',
      'toestemming',
      'permissão',
      'behörighet',
      'zugriff',
      'acceso',
      'accès',
      'accesso',
      'アクセス',
      'toegang',
      'acesso',
      'åtkomst',
    ],
    gate: { brandingFlag: 'hideAccountMembersPage' },
  },

  // Settings — Rewards (OptiSigns program — hides on white-label)
  {
    id: 'settings.referral',
    titleEnglish: 'Refer & Earn',
    titleOtherLanguages: [
      'Empfehlen & Verdienen',
      'Refiere y gana',
      'Parrainer et gagner',
      'Consiglia e guadagna',
      '紹介して報酬を獲得',
      'Aanbevelen en verdienen',
      'Indique e ganhe',
      'Rekommendera & tjäna',
    ],
    crumbs: ['Settings', 'Rewards', 'Refer & Earn'],
    route: '/app/s/referral',
    keywordsEnglish: ['refer', 'referral', 'earn', 'reward', 'invite', 'affiliate', 'partner'],
    keywordsOtherLanguages: [
      'empfehlen',
      'recomendar',
      'parrainer',
      'raccomandare',
      '紹介',
      'aanbevelen',
      'indicar',
      'rekommendera',
      'empfehlung',
      'recomendación',
      'parrainage',
      'raccomandazione',
      'aanbeveling',
      'indicação',
      'rekommendation',
      'verdienen',
      'ganar',
      'gagner',
      'guadagnare',
      '獲得',
      'ganhar',
      'tjäna',
      'belohnung',
      'recompensa',
      'récompense',
      'ricompensa',
      '報酬',
      'beloning',
      'belöning',
      'einladen',
      'invitar',
      'inviter',
      'invitare',
      '招待',
      'uitnodigen',
      'convidar',
      'bjuda in',
    ],
    gate: { whiteLabelHidden: true },
  },

  // Settings — Billing & Orders
  {
    id: 'settings.subscription-plan',
    titleEnglish: 'Subscription Plan',
    titleOtherLanguages: [
      'Abonnementplan',
      'Plan de suscripción',
      "Formule d'abonnement",
      'Piano di abbonamento',
      'サブスクリプションプラン',
      'Abonnementsplan',
      'Plano de assinatura',
      'Prenumerationsplan',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Subscription Plan'],
    route: '/app/s/subscription-plan',
    keywordsEnglish: [
      'subscription',
      'plan',
      'upgrade',
      'downgrade',
      'pricing',
      'tier',
      'standard',
      'pro',
      'engage',
      'billing',
    ],
    keywordsOtherLanguages: [
      'abonnement',
      'suscripción',
      'abbonamento',
      'サブスクリプション',
      'assinatura',
      'prenumeration',
      'piano',
      'プラン',
      'plano',
      'preisgestaltung',
      'precios',
      'tarification',
      'prezzi',
      '価格設定',
      'prijzen',
      'preços',
      'priser',
      'stufe',
      'nivel',
      'niveau',
      'livello',
      'ティア',
      'nível',
      'nivå',
      'abrechnung',
      'facturación',
      'facturation',
      'fatturazione',
      '請求',
      'facturering',
      'faturamento',
      'fakturering',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hideSubscriptionPages' },
  },
  {
    id: 'settings.payment',
    titleEnglish: 'Payment Settings',
    titleOtherLanguages: [
      'Zahlungseinstellungen',
      'Configuración de pago',
      'Paramètres de paiement',
      'Impostazioni di pagamento',
      '支払い設定',
      'Betaalinstellingen',
      'Configurações de pagamento',
      'Betalningsinställningar',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Payment Settings'],
    route: '/app/s/payment',
    keywordsEnglish: ['payment', 'card', 'credit', 'billing', 'method'],
    keywordsOtherLanguages: [
      'zahlung',
      'pago',
      'paiement',
      'pagamento',
      '支払い',
      'betaling',
      'betalning',
      'karte',
      'tarjeta',
      'carte',
      'carta',
      'カード',
      'kaart',
      'cartão',
      'kort',
      'kredit',
      'crédito',
      'crédit',
      'クレジット',
      'krediet',
      'abrechnung',
      'facturación',
      'facturation',
      'fatturazione',
      '請求',
      'facturering',
      'faturamento',
      'fakturering',
      'methode',
      'método',
      'méthode',
      'metodo',
      '方法',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hideSubscriptionPages' },
  },
  {
    id: 'settings.payment-history',
    titleEnglish: 'Billing History',
    titleOtherLanguages: [
      'Rechnungsverlauf',
      'Historial de facturación',
      'Historique de facturation',
      'Cronologia di fatturazione',
      '請求履歴',
      'Factuurgeschiedenis',
      'Histórico de faturamento',
      'Faktureringshistorik',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Billing History'],
    route: '/app/s/payment-history',
    keywordsEnglish: [
      'invoice',
      'invoices',
      'receipt',
      'receipts',
      'history',
      'billing',
      'payment',
      'charge',
    ],
    keywordsOtherLanguages: [
      'rechnung',
      'factura',
      'facture',
      'fattura',
      '請求書',
      'factuur',
      'fatura',
      'faktura',
      'rechnungen',
      'facturas',
      'factures',
      'fatture',
      'facturen',
      'faturas',
      'fakturor',
      'quittung',
      'recibo',
      'reçu',
      'ricevuta',
      '領収書',
      'ontvangstbewijs',
      'kvitto',
      'verlauf',
      'historial',
      'historique',
      'cronologia',
      '履歴',
      'geschiedenis',
      'histórico',
      'historik',
      'abrechnung',
      'facturación',
      'facturation',
      'fatturazione',
      '請求',
      'facturering',
      'faturamento',
      'fakturering',
      'zahlung',
      'pago',
      'paiement',
      'pagamento',
      '支払い',
      'betaling',
      'betalning',
      'belastung',
      'cargo',
      'frais',
      'addebito',
      'heffing',
      'cobrança',
      'avgift',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hidePaymentHistoryPages' },
  },
  {
    id: 'settings.sub-account-billing',
    titleEnglish: 'Sub-Account Billing',
    titleOtherLanguages: [
      'Unterkontoabrechnung',
      'Facturación de subcuentas',
      'Facturation des sous-comptes',
      'Fatturazione del sottoconto',
      'サブアカウント請求',
      'Facturering via subrekening',
      'Faturamento de subconta',
      'Fakturering för underkonton',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Sub-Account Billing'],
    route: '/app/s/sub-account-billing',
    keywordsEnglish: ['sub', 'account', 'billing', 'children', 'parent', 'partner'],
    keywordsOtherLanguages: [
      'konto',
      'cuenta',
      'compte',
      'conto',
      'アカウント',
      'conta',
      'abrechnung',
      'facturación',
      'facturation',
      'fatturazione',
      '請求',
      'facturering',
      'faturamento',
      'fakturering',
      'kinder',
      'hijos',
      'enfants',
      'figli',
      '子',
      'kinderen',
      'filhos',
      'barn',
      'eltern',
      'padre',
      'genitore',
      '親',
      'ouder',
      'pai',
      'förälder',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true, childAccountHidden: true },
  },
  {
    id: 'settings.coupons',
    titleEnglish: 'Coupons',
    titleOtherLanguages: [
      'Gutscheine',
      'Cupones',
      'Coupons',
      'Buoni sconto',
      'クーポン',
      'Coupons',
      'Cupons',
      'Kuponger',
    ],
    crumbs: ['Settings', 'Billing & Orders', 'Coupons'],
    route: '/app/s/coupons',
    keywordsEnglish: ['coupon', 'coupons', 'discount', 'promo', 'code'],
    keywordsOtherLanguages: [
      'gutschein',
      'cupón',
      'cupom',
      'kupong',
      'gutscheine',
      'cupones',
      'cupons',
      'kuponger',
      'rabatt',
      'descuento',
      'remise',
      'sconto',
      '割引',
      'korting',
      'desconto',
      'código',
      'codice',
      'コード',
      'kod',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true, defaultTeamOnly: true },
  },

  // Settings — Settings
  {
    id: 'settings.preferences',
    titleEnglish: 'Preferences',
    titleOtherLanguages: [
      'Präferenzen',
      'Preferencias',
      'Préférences',
      'Preferenze',
      '設定',
      'Voorkeuren',
      'Preferências',
      'Inställningar',
    ],
    crumbs: ['Settings', 'Settings', 'Preferences'],
    route: '/app/s/preference-settings',
    keywordsEnglish: ['preference', 'preferences', 'setting', 'options', 'display', 'default'],
    keywordsOtherLanguages: [
      'präferenz',
      'preferencia',
      'préférence',
      'preferenza',
      '環境設定',
      'voorkeur',
      'preferência',
      'inställning',
      'präferenzen',
      'preferencias',
      'préférences',
      'preferenze',
      'voorkeuren',
      'preferências',
      'inställningar',
      'einstellung',
      'configuración',
      'paramètre',
      'impostazione',
      '設定',
      'instelling',
      'configuração',
      'optionen',
      'opciones',
      'opzioni',
      'オプション',
      'opties',
      'opções',
      'alternativ',
      'anzeige',
      'pantalla',
      'affichage',
      'schermo',
      'ディスプレイ',
      'weergave',
      'exibição',
      'predeterminado',
      'défaut',
      'predefinito',
      'デフォルト',
      'standaard',
      'padrão',
    ],
    gate: { whiteLabelHidden: true, minRole: 'superAdmin' },
  },
  {
    id: 'settings.operational-schedule',
    titleEnglish: 'Operational Schedule',
    titleOtherLanguages: [
      'Betriebsplan',
      'Calendario operativo',
      'Calendrier opérationnel',
      'Programma operativo',
      '運行スケジュール',
      'Operationeel schema',
      'Cronograma operacional',
      'Driftschema',
    ],
    crumbs: ['Settings', 'Settings', 'Operational Schedule'],
    route: '/app/s/operational-schedule',
    keywordsEnglish: ['operational', 'schedule', 'hours', 'business', 'open', 'close', 'onhold'],
    keywordsOtherLanguages: [
      'betrieblich',
      'operativo',
      'opérationnel',
      '運用',
      'operationeel',
      'operacional',
      'operativ',
      'zeitplan',
      'horario',
      'horaire',
      'orario',
      'スケジュール',
      'schema',
      'horário',
      'stunden',
      'horas',
      'heures',
      'ore',
      '時間',
      'uren',
      'timmar',
      'geschäft',
      'negocio',
      'entreprise',
      'ビジネス',
      'öffnen',
      'abrir',
      'ouvrir',
      'aprire',
      '開く',
      'openen',
      'öppna',
      'schließen',
      'cerrar',
      'fermer',
      'chiudere',
      '閉じる',
      'sluiten',
      'fechar',
      'stänga',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },

  // Settings — Data & Monitoring
  {
    id: 'settings.data-sources',
    titleEnglish: 'Data Sources',
    titleOtherLanguages: [
      'Datenquellen',
      'Fuentes de datos',
      'Sources de données',
      'Fonti dei dati',
      'データソース',
      'Gegevensbronnen',
      'Fontes de dados',
      'Datakällor',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Data Sources'],
    route: '/app/s/datasources-admin',
    keywordsEnglish: ['data', 'source', 'datasource', 'csv', 'google', 'sheet', 'sheets', 'excel'],
    keywordsOtherLanguages: [
      'daten',
      'datos',
      'données',
      'dati',
      'データ',
      'gegevens',
      'dados',
      'quelle',
      'fuente',
      'fonte',
      'ソース',
      'bron',
      'källa',
      'blatt',
      'hoja',
      'feuille',
      'foglio',
      'シート',
      'blad',
      'folha',
      'blätter',
      'hojas',
      'feuilles',
      'fogli',
      'bladen',
      'folhas',
    ],
    gate: { brandingRemovedHidden: true, minRole: 'admin' },
  },
  {
    id: 'settings.feeds',
    titleEnglish: 'Feeds',
    titleOtherLanguages: [
      'Feeds',
      'Fuentes',
      'Flux',
      'Feed',
      'フィード',
      'Feeds',
      'Feeds',
      'Flöden',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Feeds'],
    route: '/app/s/feeds-admin',
    keywordsEnglish: ['feed', 'feeds', 'rss', 'social', 'facebook', 'instagram'],
    keywordsOtherLanguages: [
      'feed',
      'flux',
      'フィード',
      'flöde',
      'fuentes',
      'flöden',
      'sozial',
      'social',
      'sociale',
      'ソーシャル',
      'sociaal',
      'facebook',
      'nachrichten',
      'noticias',
      'actualités',
      'notizie',
      'ニュース',
      'nieuws',
      'notícias',
      'nyheter',
    ],
    gate: { minRole: 'rootAdmin', feature: 'feedsCreate' },
  },
  {
    id: 'settings.share-links',
    titleEnglish: 'Share Links',
    titleOtherLanguages: [
      'Links teilen',
      'Compartir enlaces',
      'Partager des liens',
      'Condividi i link',
      '共有リンク',
      'Deel links',
      'Compartilhar links',
      'Dela länkar',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Share Links'],
    route: '/app/s/share-links-admin',
    keywordsEnglish: ['share', 'link', 'links', 'external', 'anonymous', 'public'],
    keywordsOtherLanguages: [
      'teilen',
      'compartir',
      'partager',
      'condividere',
      '共有',
      'delen',
      'compartilhar',
      'dela',
      'enlace',
      'lien',
      'リンク',
      'länk',
      'enlaces',
      'liens',
      'länkar',
      'extern',
      'externo',
      'externe',
      'esterno',
      '外部',
      'anonym',
      'anónimo',
      'anonyme',
      'anonimo',
      '匿名',
      'anoniem',
      'anônimo',
      'öffentlich',
      'público',
      'pubblico',
      '公開',
      'openbaar',
      'offentlig',
    ],
    gate: { feature: 'shareLink', brandingRemovedHidden: true, minRole: 'admin' },
  },
  {
    id: 'settings.proof-of-play',
    titleEnglish: 'Proof of Play',
    titleOtherLanguages: [
      'Spielnachweis',
      'Prueba de reproducción',
      'Preuve de diffusion',
      'Prova di riproduzione',
      '再生証明',
      'Bewijs van weergave',
      'Prova de reprodução',
      'Bevis på uppspelning',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Proof of Play'],
    route: '/app/s/proof-of-play-settings',
    keywordsEnglish: ['proof', 'play', 'audit', 'report', 'playback', 'log'],
    keywordsOtherLanguages: [
      'nachweis',
      'prueba',
      'preuve',
      'prova',
      '証明',
      'bewijs',
      'bevis',
      'abspielen',
      'reproducir',
      'lecture',
      'riproduzione',
      '再生',
      'afspelen',
      'reprodução',
      'uppspelning',
      'prüfung',
      'auditoría',
      '監査',
      'auditoria',
      'granskning',
      'bericht',
      'informe',
      'rapport',
      'rapporto',
      'レポート',
      'relatório',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hideProofofPlay' },
  },
  {
    id: 'settings.monitoring-alerts',
    titleEnglish: 'Monitoring Alerts',
    titleOtherLanguages: [
      'Überwachungsalarme',
      'Alertas de monitoreo',
      'Alertes de surveillance',
      'Monitoraggio degli avvisi',
      '監視アラート',
      'Monitoringwaarschuwingen',
      'Alertas de monitoramento',
      'Övervakningsvarningar',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Monitoring Alerts'],
    route: '/app/s/monitoring-alert-rules',
    keywordsEnglish: [
      'monitor',
      'monitoring',
      'alert',
      'alerts',
      'notification',
      'offline',
      'down',
    ],
    keywordsOtherLanguages: [
      'moniteur',
      'モニター',
      'skärm',
      'überwachung',
      'monitoreo',
      'surveillance',
      'monitoraggio',
      '監視',
      'monitoramento',
      'övervakning',
      'warnung',
      'alerta',
      'alerte',
      'avviso',
      'アラート',
      'waarschuwing',
      'varning',
      'warnungen',
      'alertas',
      'alertes',
      'avvisi',
      'waarschuwingen',
      'varningar',
      'benachrichtigung',
      'notificación',
      'notifica',
      '通知',
      'melding',
      'notificação',
      'meddelande',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hideMonitorAndAlert' },
  },
  {
    id: 'settings.audit-log',
    titleEnglish: 'Audit Log',
    titleOtherLanguages: [
      'Prüfprotokoll',
      'Registro de auditoría',
      "Journal d'audit",
      'Registro di controllo',
      '監査ログ',
      'Auditlogboek',
      'Registro de auditoria',
      'Granskningslogg',
    ],
    crumbs: ['Settings', 'Data & Monitoring', 'Audit Log'],
    route: '/app/security-audit-logging',
    keywordsEnglish: ['audit', 'log', 'security', 'history', 'action', 'compliance'],
    keywordsOtherLanguages: [
      'prüfung',
      'auditoría',
      '監査',
      'auditoria',
      'granskning',
      'sicherheit',
      'seguridad',
      'sécurité',
      'sicurezza',
      'セキュリティ',
      'beveiliging',
      'segurança',
      'säkerhet',
      'verlauf',
      'historial',
      'historique',
      'cronologia',
      '履歴',
      'geschiedenis',
      'histórico',
      'historik',
      'aktion',
      'acción',
      'azione',
      'アクション',
      'actie',
      'ação',
      'åtgärd',
    ],
    gate: { minRole: 'rootAdmin', brandingFlag: 'hideAuditLogging' },
  },

  // Settings — Advanced
  {
    id: 'settings.branding',
    titleEnglish: 'Branding Settings',
    titleOtherLanguages: [
      'Branding-Einstellungen',
      'Configuración de marca',
      'Paramètres de marque',
      'Impostazioni del marchio',
      'ブランド設定',
      'Merkinstellingen',
      'Configurações de marca',
      'Varumärkesinställningar',
    ],
    crumbs: ['Settings', 'Advanced', 'Branding Settings'],
    route: '/app/s/branding-settings',
    keywordsEnglish: ['brand', 'branding', 'white', 'label', 'logo', 'custom', 'domain', 'theme'],
    keywordsOtherLanguages: [
      'marke',
      'marca',
      'marque',
      'ブランド',
      'merk',
      'varumärke',
      'image de marque',
      'ブランディング',
      'weiß',
      'blanco',
      'blanc',
      'bianco',
      'ホワイト',
      'wit',
      'branco',
      'vit',
      'beschriftung',
      'etiqueta',
      'étiquette',
      'etichetta',
      'ラベル',
      'rótulo',
      'etikett',
      'benutzerdefiniert',
      'personalizado',
      'personnalisé',
      'personalizzato',
      'カスタム',
      'aangepast',
      'anpassad',
      'thema',
      'tema',
      'thème',
      'テーマ',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },
  {
    id: 'settings.saml',
    titleEnglish: 'SAML Single Sign On',
    titleOtherLanguages: [
      'SAML Single Sign-On',
      'Inicio de sesión único SAML',
      'Authentification unique SAML',
      'Autenticazione singola SAML',
      'SAMLシングルサインオン',
      'SAML eenmalige aanmelding',
      'Autenticação única SAML',
      'SAML enkel inloggning',
    ],
    crumbs: ['Settings', 'Advanced', 'SAML Single Sign On'],
    route: '/app/s/saml-settings',
    keywordsEnglish: ['saml', 'sso', 'single', 'sign', 'on', 'okta', 'azure', 'auth', 'idp'],
    keywordsOtherLanguages: [
      'einzel',
      'único',
      'unique',
      'singolo',
      'シングル',
      'enkelvoudig',
      'enkel',
      'anmelden',
      'iniciar sesión',
      'se connecter',
      'accedere',
      'サインイン',
      'aanmelden',
      'entrar',
      'logga in',
      'an',
      'en',
      'sur',
      'su',
      'の上',
      'op',
      'sobre',
      'på',
      'authentifizierung',
      'autenticación',
      'authentification',
      'autenticazione',
      '認証',
      'authenticatie',
      'autenticação',
      'autentisering',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },
  {
    id: 'settings.external-coms',
    titleEnglish: 'External Communications',
    titleOtherLanguages: [
      'Externe Kommunikation',
      'Comunicaciones externas',
      'Communications externes',
      'Comunicazioni esterne',
      '外部コミュニケーション',
      'Externe communicatie',
      'Comunicação externa',
      'Extern kommunikation',
    ],
    crumbs: ['Settings', 'Advanced', 'External Communications'],
    route: '/app/s/external-coms',
    keywordsEnglish: ['external', 'communication', 'communications', 'webhook', 'api'],
    keywordsOtherLanguages: [
      'extern',
      'externo',
      'externe',
      'esterno',
      '外部',
      'kommunikation',
      'comunicación',
      'communication',
      'comunicazione',
      'コミュニケーション',
      'communicatie',
      'comunicação',
      'benachrichtigung',
      'notificación',
      'notification',
      'notifica',
      '通知',
      'melding',
      'notificação',
      'avisering',
      'webhook',
    ],
    gate: { minRole: 'admin', brandingFlag: 'hideExternalCommunications' },
  },
  {
    id: 'settings.locations',
    titleEnglish: 'Functional Locations',
    titleOtherLanguages: [
      'Funktionale Standorte',
      'Ubicaciones funcionales',
      'Emplacements fonctionnels',
      'Posizioni funzionali',
      '機能的な場所',
      'Functionele locaties',
      'Localizações funcionais',
      'Funktionella platser',
    ],
    crumbs: ['Settings', 'Advanced', 'Functional Locations'],
    route: '/app/s/functional-locations',
    keywordsEnglish: ['location', 'locations', 'site', 'sites', 'geo', 'address', 'store'],
    keywordsOtherLanguages: [
      'standort',
      'ubicación',
      'emplacement',
      'posizione',
      '場所',
      'locatie',
      'localização',
      'plats',
      'standorte',
      'ubicaciones',
      'emplacements',
      'posizioni',
      'locaties',
      'localizações',
      'platser',
      'sitio',
      'サイト',
      'sitios',
      'adresse',
      'dirección',
      'indirizzo',
      '住所',
      'adres',
      'endereço',
      'adress',
      'laden',
      'tienda',
      'magasin',
      'negozio',
      '店舗',
      'winkel',
      'loja',
      'butik',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },
  {
    id: 'settings.api-keys',
    titleEnglish: 'API Keys',
    titleOtherLanguages: [
      'API-Schlüssel',
      'Claves de API',
      'Clés API',
      'Chiavi API',
      'APIキー',
      'API-sleutels',
      'Chaves de API',
      'API-nycklar',
    ],
    crumbs: ['Settings', 'Advanced', 'API Keys'],
    route: '/app/s/apikeys',
    keywordsEnglish: ['api', 'key', 'keys', 'token', 'access', 'developer'],
    keywordsOtherLanguages: [
      'schlüssel',
      'clave',
      'clé',
      'chiave',
      'キー',
      'sleutel',
      'chave',
      'nyckel',
      'claves',
      'clés',
      'chiavi',
      'sleutels',
      'chaves',
      'nycklar',
      'zugriff',
      'acceso',
      'accès',
      'accesso',
      'アクセス',
      'toegang',
      'acesso',
      'åtkomst',
      'entwickler',
      'desarrollador',
      'développeur',
      'sviluppatore',
      '開発者',
      'ontwikkelaar',
      'desenvolvedor',
      'utvecklare',
    ],
    gate: { minRole: 'rootAdmin', whiteLabelHidden: true },
  },
  {
    id: 'settings.integrations',
    titleEnglish: 'Integrations',
    titleOtherLanguages: [
      'Integrationen',
      'Integraciones',
      'Intégrations',
      'Integrazioni',
      '統合',
      'Integraties',
      'Integrações',
      'Integrationer',
    ],
    crumbs: ['Settings', 'Advanced', 'Integrations'],
    route: '/app/s/integrations',
    keywordsEnglish: [
      'integration',
      'integrations',
      'google',
      'microsoft',
      'facebook',
      'tableau',
      'powerbi',
      'oauth',
      'connect',
    ],
    keywordsOtherLanguages: [
      'integration',
      'integración',
      'intégration',
      'integrazione',
      '統合',
      'integratie',
      'integração',
      'integrationen',
      'integraciones',
      'intégrations',
      'integrazioni',
      'integraties',
      'integrações',
      'integrationer',
      'facebook',
      'tableau',
      'cuadro',
      'タブロー',
      'quadro',
      'tablå',
      'verbinden',
      'conectar',
      'connecter',
      'connettere',
      '接続',
      'ansluta',
      'dienst',
      'servicio',
      'service',
      'servizio',
      'サービス',
      'serviço',
      'tjänst',
      'verbindung',
      'conexión',
      'connexion',
      'connessione',
      '接続',
      'verbinding',
      'conexão',
      'anslutning',
    ],
    gate: { brandingRemovedHidden: true, minRole: 'admin' },
  },
  {
    id: 'settings.revue-workflow',
    titleEnglish: 'Revue Workflow',
    crumbs: ['Settings', 'Advanced', 'Revue Workflow'],
    route: '/app/s/revue',
    keywordsEnglish: ['revue', 'workflow', 'approval', 'review'],
    keywordsOtherLanguages: [
      'genehmigung',
      'aprobación',
      'approbation',
      'approvazione',
      '承認',
      'goedkeuring',
      'aprovação',
      'godkännande',
      'überprüfung',
      'revisar',
      'examen',
      'recensione',
      'レビュー',
      'beoordelen',
      'revisão',
      'granska',
    ],
    gate: { minRole: 'rootAdmin' },
  },

  // ─────────────────────────────────────────────────────────────────────
  // v2 — Action entries. Each carries an `openDialog` slug the dispatcher
  // (`nav-actions.ts`) maps to a store call or `?action=` URL param.
  // Gates mirror the destination toolbar/sidebar button exactly (Access =
  // See — button hidden = search hit hidden).
  // ─────────────────────────────────────────────────────────────────────

  // Screens — actions
  {
    id: 'action.screen-pair',
    titleEnglish: 'Pair Screen',
    titleOtherLanguages: [
      'Bildschirm koppeln',
      'Emparejar pantalla',
      "Coupler l'écran",
      'Accoppia schermo',
      '画面をペアリング',
      'Scherm koppelen',
      'Emparelhar tela',
      'Parkoppla skärm',
    ],
    crumbs: ['Screens', 'Pair Screen'],
    route: '/app/screenManagement',
    openDialog: 'screen-pair',
    keywordsEnglish: ['pair', 'add', 'new', 'register', 'connect', 'device', 'setup'],
    keywordsOtherLanguages: [
      'koppeln',
      'emparejar',
      'coupler',
      'accoppiare',
      'ペアリング',
      'koppelen',
      'emparelhar',
      'parkoppla',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'registrieren',
      'registrar',
      'enregistrer',
      'registrare',
      '登録',
      'registreren',
      'registrera',
      'verbinden',
      'conectar',
      'connecter',
      'connettere',
      '接続',
      'ansluta',
      'gerät',
      'dispositivo',
      'appareil',
      'デバイス',
      'apparaat',
      'enhet',
      'einrichten',
      'configurar',
      'configurer',
      'configurare',
      'セットアップ',
      'instellen',
      'inställning',
    ],
    gate: { signageOnly: true, canCreate: 'screen' },
  },
  {
    id: 'action.screen-preconfigure',
    titleEnglish: 'Preconfigure Devices',
    titleOtherLanguages: [
      'Geräte vorkonfigurieren',
      'Preconfigurar dispositivos',
      'Préconfigurer les appareils',
      'Preconfigurare i dispositivi',
      'デバイスを事前構成',
      'Apparaten vooraf configureren',
      'Pré-configurar dispositivos',
      'Förkonfigurera enheter',
    ],
    crumbs: ['Screens', 'Preconfigure Devices'],
    route: '/app/screenManagement',
    openDialog: 'screen-preconfigure',
    keywordsEnglish: ['preconfigure', 'provision', 'setup', 'bulk', 'device', 'template'],
    keywordsOtherLanguages: [
      'bereitstellen',
      'aprovisionar',
      'provisionner',
      'provisioning',
      'プロビジョニング',
      'provisioneren',
      'provisionar',
      'provisionera',
      'einrichten',
      'configurar',
      'configurer',
      'configurare',
      'セットアップ',
      'instellen',
      'inställning',
      'massen',
      'masivo',
      'en masse',
      'in blocco',
      '一括',
      'bulk',
      'em massa',
      'gerät',
      'dispositivo',
      'appareil',
      'デバイス',
      'apparaat',
      'enhet',
    ],
    gate: { signageOnly: true, canCreate: 'screen', feature: 'advancedDeviceManagement' },
  },
  {
    id: 'action.screen-execute',
    titleEnglish: 'Execute Remote Commands',
    titleOtherLanguages: [
      'Remote-Befehle ausführen',
      'Ejecutar comandos remotos',
      'Exécuter des commandes à distance',
      'Esegui comandi remoti',
      'リモートコマンドを実行',
      'Externe opdrachten uitvoeren',
      'Executar comandos remotos',
      'Kör fjärrkommandon',
    ],
    crumbs: ['Screens', 'Execute Remote Commands'],
    route: '/app/screenManagement',
    openDialog: 'screen-execute',
    keywordsEnglish: ['execute', 'remote', 'command', 'run', 'terminal', 'shell', 'adb'],
    keywordsOtherLanguages: [
      'ausführen',
      'ejecutar',
      'exécuter',
      'eseguire',
      '実行',
      'uitvoeren',
      'executar',
      'köra',
      'remote',
      'remoto',
      'distant',
      'リモート',
      'op afstand',
      'fjärr',
      'befehl',
      'comando',
      'commande',
      'コマンド',
      'opdracht',
      'kommando',
    ],
    gate: { signageOnly: true, minRole: 'admin', feature: 'remoteCommand' },
  },
  {
    id: 'action.screen-export-devices-list',
    titleEnglish: 'Export Devices List',
    titleOtherLanguages: [
      'Geräteliste exportieren',
      'Exportar lista de dispositivos',
      'Exporter la liste des appareils',
      'Esporta elenco dispositivi',
      'デバイスリストをエクスポート',
      'Apparatenlijst exporteren',
      'Exportar lista de dispositivos',
      'Exportera enhetslista',
    ],
    crumbs: ['Screens', 'Export Devices List'],
    route: '/app/screenManagement',
    openDialog: 'screen-export-devices-list',
    keywordsEnglish: ['export', 'download', 'csv', 'devices', 'list', 'report'],
    keywordsOtherLanguages: [
      'exportieren',
      'exportar',
      'exporter',
      'esportare',
      'エクスポート',
      'exporteren',
      'exportera',
      'herunterladen',
      'descargar',
      'télécharger',
      'scaricare',
      'ダウンロード',
      'downloaden',
      'baixar',
      'ladda ner',
      'liste',
      'lista',
      'elenco',
      'リスト',
      'lijst',
      'bericht',
      'informe',
      'rapport',
      'rapporto',
      'レポート',
      'relatório',
    ],
    gate: { signageOnly: true },
  },

  // Assets — actions
  {
    id: 'action.asset-upload',
    titleEnglish: 'Upload Files',
    titleOtherLanguages: [
      'Dateien hochladen',
      'Cargar archivos',
      'Téléverser des fichiers',
      'Carica file',
      'ファイルをアップロード',
      'Bestanden uploaden',
      'Carregar arquivos',
      'Ladda upp filer',
    ],
    crumbs: ['Files/Assets', 'Upload Files'],
    route: '/app/assetManagement',
    openDialog: 'asset-upload',
    keywordsEnglish: ['upload', 'file', 'add', 'image', 'video', 'pdf', 'import'],
    keywordsOtherLanguages: [
      'hochladen',
      'cargar',
      'téléverser',
      'caricare',
      'アップロード',
      'uploaden',
      'carregar',
      'ladda upp',
      'datei',
      'archivo',
      'déposer',
      'file',
      'ファイル',
      'bestand',
      'arquivo',
      'fil',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'bild',
      'imagen',
      'image',
      'immagine',
      '画像',
      'afbeelding',
      'imagem',
      'video',
      'vidéo',
      'ビデオ',
      'vídeo',
      'importieren',
      'importar',
      'importer',
      'importare',
      'インポート',
      'importeren',
      'importera',
    ],
    gate: { signageOnly: true, canCreate: 'asset' },
  },
  {
    id: 'action.asset-add-app',
    titleEnglish: 'Add App',
    titleOtherLanguages: [
      'App hinzufügen',
      'Añadir aplicación',
      'Ajouter une application',
      'Aggiungi app',
      'アプリを追加',
      'App toevoegen',
      'Adicionar app',
      'Lägg till app',
    ],
    crumbs: ['Files/Assets', 'Add App'],
    // Engage has an "Add App" too — always say which one. See `alwaysQualify`.
    alwaysQualify: true,
    route: '/app/assetManagement',
    openDialog: 'app-selector',
    keywordsEnglish: ['app', 'add', 'new', 'weather', 'youtube', 'canva', 'designer', 'social'],
    keywordsOtherLanguages: [
      'app',
      'aplicación',
      'application',
      'アプリ',
      'aplicativo',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'wetter',
      'clima',
      'météo',
      'meteo',
      '天気',
      'weer',
      'väder',
      'designer',
      'diseñador',
      'デザイナー',
      'ontwerper',
      'sozial',
      'social',
      'sociale',
      'sociaal',
    ],
    gate: { signageOnly: true, canCreate: 'asset' },
  },
  // Choose Template action — dropped in favor of merging its keywords
  // (`choose`, `canvas`, `pre-built`, `gallery`) into the v1 `nav.templates`
  // entry above. Both would have pointed to `/app/templateManagement` with
  // identical gates, so keeping them separate would surface as duplicate
  // rows on a "template" search. See design doc §"v2 walkthrough — Assets".
  {
    id: 'action.asset-feeds',
    titleEnglish: 'Browse Feeds',
    titleOtherLanguages: [
      'Feeds durchsuchen',
      'Explorar feeds',
      'Parcourir les flux',
      'Sfoglia i feed',
      'フィードを閲覧',
      'Bladeren door feeds',
      'Explorar feeds',
      'Bläddra flöden',
    ],
    crumbs: ['Files/Assets', 'Browse Feeds'],
    route: '/app/assetManagement',
    openDialog: 'asset-feeds',
    keywordsEnglish: ['feed', 'feeds', 'rss', 'news', 'sports', 'weather'],
    keywordsOtherLanguages: [
      'feed',
      'flux',
      'フィード',
      'flöde',
      'flöden',
      'nachrichten',
      'noticias',
      'actualités',
      'notizie',
      'ニュース',
      'nieuws',
      'notícias',
      'nyheter',
      'sport',
      'deportes',
      'sports',
      'スポーツ',
      'esportes',
      'wetter',
      'clima',
      'météo',
      'meteo',
      '天気',
      'weer',
      'väder',
    ],
    gate: { signageOnly: true, canCreate: 'asset', brandingRemovedHidden: true },
  },

  // Playlists — actions
  {
    id: 'action.playlist-create',
    titleEnglish: 'Create Playlist',
    titleOtherLanguages: [
      'Playlist erstellen',
      'Crear lista de reproducción',
      'Créer une liste de lecture',
      'Crea playlist',
      'プレイリストを作成',
      'Afspeellijst maken',
      'Criar playlist',
      'Skapa spellista',
    ],
    crumbs: ['Playlists', 'Create Playlist'],
    route: '/app/playlistManagement',
    openDialog: 'playlist-create',
    keywordsEnglish: ['create', 'new', 'add', 'playlist', 'rotation'],
    keywordsOtherLanguages: [
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'wiedergabeliste',
      'lista de reproducción',
      'liste de lecture',
      'playlist',
      'プレイリスト',
      'afspeellijst',
      'lista de reprodução',
      'spellista',
      'rotation',
      'rotación',
      'rotazione',
      'ローテーション',
      'rotatie',
      'rotação',
    ],
    gate: { signageOnly: true, canCreate: 'playlist' },
  },

  // Schedules — actions
  {
    id: 'action.schedule-create',
    titleEnglish: 'Create Schedule',
    titleOtherLanguages: [
      'Zeitplan erstellen',
      'Crear horario',
      'Créer un horaire',
      'Crea programma',
      'スケジュールを作成',
      'Rooster maken',
      'Criar horário',
      'Skapa schema',
    ],
    crumbs: ['Schedules', 'Create Schedule'],
    route: '/app/scheduleManagement',
    openDialog: 'schedule-create',
    keywordsEnglish: ['create', 'new', 'schedule', 'calendar', 'event'],
    keywordsOtherLanguages: [
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'zeitplan',
      'cronograma',
      'calendrier',
      'programma',
      'スケジュール',
      'schema',
      'agendar',
      'ereignis',
      'evento',
      'événement',
      'イベント',
      'evenement',
      'händelse',
    ],
    gate: { signageOnly: true, canCreate: 'schedule' },
  },
  {
    id: 'action.schedule-global-default',
    titleEnglish: 'Global Default Content',
    titleOtherLanguages: [
      'Globaler Standardinhalt',
      'Contenido predeterminado global',
      'Contenu par défaut global',
      'Contenuto predefinito globale',
      'グローバルデフォルトコンテンツ',
      'Globale standaardinhoud',
      'Conteúdo padrão global',
      'Globalt standardinnehåll',
    ],
    crumbs: ['Schedules', 'Global Default Content'],
    route: '/app/scheduleManagement',
    openDialog: 'schedule-global-default',
    keywordsEnglish: ['default', 'global', 'fallback', 'content', 'settings'],
    keywordsOtherLanguages: [
      'standard',
      'por defecto',
      'défaut',
      'predefinito',
      'デフォルト',
      'standaard',
      'padrão',
      'inhalt',
      'contenido',
      'contenu',
      'contenuto',
      'コンテンツ',
      'inhoud',
      'conteúdo',
      'innehåll',
      'einstellungen',
      'ajustes',
      'paramètres',
      'impostazioni',
      '設定',
      'instellingen',
      'configurações',
      'inställningar',
    ],
    gate: { signageOnly: true, canCreate: 'schedule' },
  },

  // Account Members — actions
  {
    id: 'action.account-add-user',
    titleEnglish: 'Add User',
    titleOtherLanguages: [
      'Benutzer hinzufügen',
      'Añadir usuario',
      'Ajouter un utilisateur',
      'Aggiungi utente',
      'ユーザーを追加',
      'Gebruiker toevoegen',
      'Adicionar usuário',
      'Lägg till användare',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Add User'],
    route: '/app/s/account-members',
    openDialog: 'account-add-user',
    keywordsEnglish: ['add', 'user', 'create', 'member', 'account'],
    keywordsOtherLanguages: [
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'benutzer',
      'usuario',
      'utilisateur',
      'utente',
      'ユーザー',
      'gebruiker',
      'usuário',
      'användare',
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'mitglied',
      'miembro',
      'membre',
      'membro',
      'メンバー',
      'lid',
      'medlem',
      'konto',
      'cuenta',
      'compte',
      'account',
      'アカウント',
      'rekening',
      'conta',
    ],
    gate: { minRole: 'admin' },
  },
  {
    id: 'action.account-invite-member',
    titleEnglish: 'Invite Member',
    titleOtherLanguages: [
      'Mitglied einladen',
      'Invitar miembro',
      'Inviter un membre',
      'Invita membro',
      'メンバーを招待',
      'Lid uitnodigen',
      'Convidar membro',
      'Bjuda in medlem',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Invite Member'],
    route: '/app/s/account-members',
    openDialog: 'account-invite-member',
    keywordsEnglish: ['invite', 'email', 'member', 'user', 'send'],
    keywordsOtherLanguages: [
      'einladen',
      'invitar',
      'inviter',
      'invitare',
      '招待',
      'uitnodigen',
      'convidar',
      'bjuda in',
      'e-mail',
      'correo electrónico',
      'メール',
      'e-post',
      'mitglied',
      'miembro',
      'membre',
      'membro',
      'メンバー',
      'lid',
      'medlem',
      'benutzer',
      'usuario',
      'utilisateur',
      'utente',
      'ユーザー',
      'gebruiker',
      'usuário',
      'användare',
      'senden',
      'enviar',
      'envoyer',
      'inviare',
      '送信',
      'verzenden',
      'skicka',
    ],
    gate: { minRole: 'admin' },
  },
  {
    id: 'action.account-manage-roles',
    titleEnglish: 'Manage Roles',
    titleOtherLanguages: [
      'Rollen verwalten',
      'Administrar roles',
      'Gérer les rôles',
      'Gestisci ruoli',
      '役割を管理',
      'Rollen beheren',
      'Gerenciar funções',
      'Hantera roller',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Manage Roles'],
    route: '/app/s/account-members',
    openDialog: 'account-manage-roles',
    keywordsEnglish: ['role', 'roles', 'permission', 'custom', 'access'],
    keywordsOtherLanguages: [
      'rolle',
      'rol',
      'rôle',
      'ruolo',
      'ロール',
      'função',
      'roll',
      'rollen',
      'roles',
      'rôles',
      'ruoli',
      '役割',
      'funções',
      'roller',
      'berechtigung',
      'permiso',
      'autorisation',
      'permesso',
      '権限',
      'toestemming',
      'permissão',
      'behörighet',
      'benutzerdefiniert',
      'personalizado',
      'personnalisé',
      'personalizzato',
      'カスタム',
      'aangepast',
      'anpassad',
      'zugriff',
      'acceso',
      'accès',
      'accesso',
      'アクセス',
      'toegang',
      'acesso',
      'åtkomst',
    ],
    gate: { minRole: 'rootAdmin', feature: 'roleSecurity' },
  },
  {
    id: 'action.account-manage-members',
    titleEnglish: 'Manage Members',
    titleOtherLanguages: [
      'Mitglieder verwalten',
      'Administrar miembros',
      'Gérer les membres',
      'Gestisci membri',
      'メンバーを管理',
      'Leden beheren',
      'Gerenciar membros',
      'Hantera medlemmar',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Manage Members'],
    route: '/app/s/account-members',
    openDialog: 'account-manage-members',
    keywordsEnglish: ['manage', 'members', 'users', 'team', 'access'],
    keywordsOtherLanguages: [
      'verwalten',
      'administrar',
      'gérer',
      'gestire',
      '管理',
      'beheren',
      'gerenciar',
      'hantera',
      'mitglieder',
      'miembros',
      'membres',
      'membri',
      'メンバー',
      'leden',
      'membros',
      'medlemmar',
      'benutzer',
      'usuarios',
      'utilisateurs',
      'utenti',
      'ユーザー',
      'gebruikers',
      'usuários',
      'användare',
      'team',
      'equipo',
      'équipe',
      'squadra',
      'チーム',
      'equipe',
      'zugriff',
      'acceso',
      'accès',
      'accesso',
      'アクセス',
      'toegang',
      'acesso',
      'åtkomst',
    ],
    gate: { minRole: 'admin' },
  },
  {
    id: 'action.account-add-team',
    titleEnglish: 'Add Team',
    titleOtherLanguages: [
      'Team hinzufügen',
      'Añadir equipo',
      'Ajouter une équipe',
      'Aggiungi team',
      'チームを追加',
      'Team toevoegen',
      'Adicionar equipe',
      'Lägg till team',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Add Team'],
    route: '/app/s/account-members',
    openDialog: 'account-add-team',
    keywordsEnglish: ['team', 'create', 'new', 'workspace', 'group'],
    keywordsOtherLanguages: [
      'team',
      'equipo',
      'équipe',
      'squadra',
      'チーム',
      'equipe',
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'arbeitsbereich',
      'espacio de trabajo',
      'espace de travail',
      'area di lavoro',
      'ワークスペース',
      'werkruimte',
      'espaço de trabalho',
      'arbetsyta',
      'gruppe',
      'grupo',
      'groupe',
      'gruppo',
      'グループ',
      'groep',
      'grupp',
    ],
    gate: { minRole: 'superAdmin', feature: 'allowTeams' },
  },
  {
    id: 'action.account-update-team',
    titleEnglish: 'Update Team',
    titleOtherLanguages: [
      'Team aktualisieren',
      'Actualizar equipo',
      "Mettre à jour l'équipe",
      'Aggiorna team',
      'チームを更新',
      'Team bijwerken',
      'Atualizar equipe',
      'Uppdatera team',
    ],
    crumbs: ['Settings', 'Account', 'Account Members', 'Update Team'],
    route: '/app/s/account-members',
    openDialog: 'account-update-team',
    keywordsEnglish: ['update', 'edit', 'team', 'settings', 'rename', 'modify'],
    keywordsOtherLanguages: [
      'aktualisieren',
      'actualizar',
      'mettre à jour',
      'aggiornare',
      '更新',
      'bijwerken',
      'atualizar',
      'uppdatera',
      'bearbeiten',
      'editar',
      'modifier',
      'modificare',
      '編集',
      'bewerken',
      'redigera',
      'team',
      'equipo',
      'équipe',
      'squadra',
      'チーム',
      'equipe',
      'einstellungen',
      'configuración',
      'paramètres',
      'impostazioni',
      '設定',
      'instellingen',
      'configurações',
      'inställningar',
      'umbenennen',
      'renombrar',
      'renommer',
      'rinominare',
      '名前を変更',
      'hernoemen',
      'renomear',
      'byta namn',
    ],
    gate: { minRole: 'admin', nonDefaultTeamOnly: true },
  },

  // MDM — actions (all inherit MdmFeatureRoute's gate)
  {
    id: 'action.mdm-fleet-create',
    titleEnglish: 'New Fleet',
    titleOtherLanguages: [
      'Neue Flotte',
      'Nueva flota',
      'Nouvelle flotte',
      'Nuova flotta',
      '新しいフリート',
      'Nieuwe vloot',
      'Nova frota',
      'Ny flotta',
    ],
    crumbs: ['MDM', 'Fleets', 'New Fleet'],
    route: '/app/mdm/fleets',
    openDialog: 'mdm-fleet-create',
    keywordsEnglish: ['fleet', 'create', 'new', 'add', 'group', 'batch'],
    keywordsOtherLanguages: [
      'flotte',
      'flota',
      'flotta',
      'フリート',
      'vloot',
      'frota',
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'neu',
      'nuevo',
      'nouveau',
      'nuovo',
      '新しい',
      'nieuw',
      'novo',
      'ny',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'gruppe',
      'grupo',
      'groupe',
      'gruppo',
      'グループ',
      'groep',
      'grupp',
      'stapel',
      'lote',
      'lot',
      'lotto',
      'バッチ',
      'batch',
    ],
    gate: { feature: 'mdm', minRole: 'superAdmin' },
  },
  {
    id: 'action.mdm-template-create',
    titleEnglish: 'Add Provisioning Template',
    titleOtherLanguages: [
      'Bereitstellungsvorlage hinzufügen',
      'Añadir plantilla de aprovisionamiento',
      'Ajouter un modèle de provisionnement',
      'Aggiungi modello di provisioning',
      'プロビジョニングテンプレートを追加',
      'Provisioneringssjabloon toevoegen',
      'Adicionar modelo de provisionamento',
      'Lägg till provisioneringsmall',
    ],
    crumbs: ['MDM', 'Provisioning Templates', 'Add Provisioning Template'],
    route: '/app/mdm/templates',
    openDialog: 'mdm-template-create',
    keywordsEnglish: ['provision', 'provisioning', 'template', 'add', 'create', 'onboarding'],
    keywordsOtherLanguages: [
      'bereitstellen',
      'aprovisionar',
      'provisionner',
      'provisioning',
      'プロビジョニング',
      'provisioneren',
      'provisionar',
      'provisionera',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'onboarding',
      'incorporación',
      'intégration',
      'オンボーディング',
      'integração',
      'introduktion',
    ],
    gate: { feature: 'mdm', minRole: 'superAdmin' },
  },
  {
    id: 'action.mdm-wifi-create',
    titleEnglish: 'Add Wi-Fi Profile',
    titleOtherLanguages: [
      'Wi-Fi-Profil hinzufügen',
      'Añadir perfil Wi-Fi',
      'Ajouter un profil Wi-Fi',
      'Aggiungi profilo Wi-Fi',
      'Wi-Fiプロファイルを追加',
      'Wi-Fi-profiel toevoegen',
      'Adicionar perfil Wi-Fi',
      'Lägg till Wi-Fi-profil',
    ],
    crumbs: ['MDM', 'Wi-Fi & 802.1X', 'Add Wi-Fi Profile'],
    route: '/app/mdm/wifi-profiles',
    openDialog: 'mdm-wifi-create',
    keywordsEnglish: ['wifi', 'wi-fi', 'network', 'ssid', 'add', 'create', 'profile'],
    keywordsOtherLanguages: [
      'wlan',
      'wifi',
      'ワイファイ',
      'netzwerk',
      'red',
      'réseau',
      'rete',
      'ネットワーク',
      'netwerk',
      'rede',
      'nätverk',
      'hinzufügen',
      'añadir',
      'ajouter',
      'aggiungere',
      '追加',
      'toevoegen',
      'adicionar',
      'tillägga',
      'erstellen',
      'crear',
      'créer',
      'creare',
      '作成する',
      'creëren',
      'criar',
      'skapa',
      'profil',
      'perfil',
      'profilo',
      'プロフィール',
      'profiel',
    ],
    gate: { feature: 'mdm', minRole: 'superAdmin' },
  },
  {
    id: 'action.mdm-eap-create',
    titleEnglish: 'Add 802.1X Profile',
    titleOtherLanguages: [
      '802.1X-Profil hinzufügen',
      'Añadir perfil 802.1X',
      'Ajouter un profil 802.1X',
      'Aggiungi profilo 802.1X',
      '802.1Xプロファイルを追加',
      '802.1X-profiel toevoegen',
      'Adicionar perfil 802.1X',
      'Lägg till 802.1X-profil',
    ],
    crumbs: ['MDM', 'Wi-Fi & 802.1X', 'Add 802.1X Profile'],
    route: '/app/mdm/wifi-profiles',
    openDialog: 'mdm-eap-create',
    keywordsEnglish: ['802.1x', 'eap', 'enterprise', 'radius', 'certificate', 'profile', 'auth'],
    keywordsOtherLanguages: [
      'unternehmen',
      'empresa',
      'entreprise',
      'imprese',
      '企業',
      'onderneming',
      'företag',
      'zertifikat',
      'certificado',
      'certificat',
      'certificato',
      '証明書',
      'certificaat',
      'certifikat',
      'profil',
      'perfil',
      'profilo',
      'プロフィール',
      'profiel',
      'authentifizierung',
      'autenticación',
      'authentification',
      'autenticazione',
      '認証',
      'authenticatie',
      'autenticação',
      'autentisering',
    ],
    gate: { feature: 'mdm', minRole: 'superAdmin' },
  },

  // Engage — actions
  {
    id: 'action.engage-add-app',
    titleEnglish: 'Add App',
    titleOtherLanguages: [
      'App hinzufügen',
      'Añadir aplicación',
      'Ajouter une application',
      'Aggiungi app',
      'アプリを追加',
      'App toevoegen',
      'Adicionar app',
      'Lägg till app',
    ],
    crumbs: ['Engage', 'Add App'],
    // Files/Assets has an "Add App" too — always say which one. See `alwaysQualify`.
    alwaysQualify: true,
    route: '/app/engageManagement',
    openDialog: 'engage-add-app',
    keywordsEnglish: ['engage', 'app', 'new', 'create', 'add', 'wizard'],
    keywordsOtherLanguages: [
      'engagieren',
      'comprometer',
      "s'engager",
      'ingaggiare',
      '従事する',
      'erbij betrekken',
      'envolver',
      'engagera',
      'app',
      'aplicación',
      'application',
      'アプリ',
      'aplicativo',
      'anwendung',
      'applicazione',
      'toepassing',
      'applikation',
    ],
    gate: { signageOnly: true, canCreate: 'asset' },
  },
];

export interface MatchNavTargetsOptions {
  limit?: number;
  minScore?: number;
}

/**
 * Match `query` against the given nav targets. Returns the top-N highest
 * scoring hits, sorted by score desc.
 *
 * The matcher scores the query against every string in `target.titleOtherLanguages`
 * (falls back to just `title` if the field is absent) plus the English
 * `keywords` list. Cross-language search "just works" — no i18n runtime state
 * is consulted, so a French user typing "écran" hits Screens in any UI.
 *
 * The matcher does NOT filter by gates — callers must pass an already-visible
 * subset (see `useVisibleNavTargets`).
 *
 * Score bands (max across all fields — one strong signal is enough, we do NOT
 * sum matches):
 *   - Any locale title word-start   → 100
 *   - Any locale title substring    →  60
 *   - Keyword word-start            →  50
 *   - Keyword substring             →  30
 * Below `minScore` (default 30) targets are dropped. Queries < 2 chars return
 * empty (dropdown already gates the same threshold).
 */
/**
 * An entry that DOES something (create, pair, upload, invite, export) rather
 * than just navigating. `openDialog` is the discriminator, not the id prefix:
 * carrying an action slug IS what makes an entry actionable, so a future entry
 * can't drift out of the group by being named differently.
 */
export function isActionTarget(target: NavTarget): boolean {
  return Boolean(target.openDialog);
}

export interface GroupedNavHits {
  /** Actionable entries — "Pair Screen", "Create Playlist", "Upload Files". */
  actions: NavTarget[];
  /** Pure navigation — "Screens", "Billing", "Wi-Fi & 802.1X". */
  navs: NavTarget[];
}

/**
 * Same scoring as `matchNavTargets`, but partitioned into actions vs
 * navigation, each with its OWN limit.
 *
 * Why separate limits rather than one ranked list: an action and its
 * destination page score identically on the shared noun ("playlist" hits both
 * `Playlists` and `Create Playlist` at 100), so a single capped list let
 * whichever came first in the registry crowd the other out. Reserving slots
 * per kind means the create path is always offered — you don't have to guess
 * the verb ("pair" vs "add" vs "new") to discover it exists.
 */
/**
 * Display labels for a list of ACTION rows, keyed by target id.
 *
 * An action row says the verb and nothing else — "Create Playlist", not
 * "Playlists › Create Playlist". Under an "Actions" heading the parent is
 * noise, and the crumb buries the word the user is scanning for.
 *
 * Two exceptions fall back to the full crumb path:
 *
 * 1. A runtime collision — two visible actions translating to the SAME title
 *    would render two identical rows with no way to tell them apart.
 * 2. `alwaysQualify` — the title is ambiguous in the PRODUCT, so it needs
 *    qualifying even when it is the only match. "Add App" exists on both
 *    Files/Assets and Engage and opens a different picker on each; searching
 *    "engage" matches only the Engage one, so the collision check never fired
 *    and the row read as a bare "Add App" with nothing saying which.
 *
 * Everything else stays bare.
 */
export function getActionLabels(
  targets: NavTarget[],
  translate: (key: string) => string,
): Map<string, string> {
  const titles = targets.map((target) => translate(target.titleEnglish));
  const counts = new Map<string, number>();
  for (const title of titles) counts.set(title, (counts.get(title) ?? 0) + 1);

  return new Map(
    targets.map((target, i) => {
      const title = titles[i] ?? target.titleEnglish;
      const needsCrumbs = target.alwaysQualify || (counts.get(title) ?? 0) > 1;
      const label = needsCrumbs ? target.crumbs.map(translate).join(' › ') : title;
      return [target.id, label];
    }),
  );
}

export function matchNavTargetsGrouped(
  query: string,
  targets: NavTarget[] = NAV_TARGETS,
  options: { actionLimit?: number; navLimit?: number; minScore?: number } = {},
): GroupedNavHits {
  const { actionLimit = 4, navLimit = 5, minScore = 30 } = options;
  // Rank once over everything, then split — so ordering WITHIN each group
  // still reflects true relevance.
  const ranked = matchNavTargets(query, targets, {
    limit: Number.POSITIVE_INFINITY,
    minScore,
  });
  return {
    actions: ranked.filter(isActionTarget).slice(0, actionLimit),
    navs: ranked.filter((target) => !isActionTarget(target)).slice(0, navLimit),
  };
}

export function matchNavTargets(
  query: string,
  targets: NavTarget[] = NAV_TARGETS,
  options: MatchNavTargetsOptions = {},
): NavTarget[] {
  const { limit = 5, minScore = 30 } = options;
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  const scored: { target: NavTarget; score: number }[] = [];
  for (const target of targets) {
    const score = scoreTarget(target, q);
    if (score >= minScore) scored.push({ target, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.target);
}

function scoreTarget(target: NavTarget, q: string): number {
  let best = 0;

  // English title + every locale translation. One vocabulary, scored
  // identically — the matcher doesn't care which locale a string is from.
  const titles = [target.titleEnglish, ...(target.titleOtherLanguages ?? [])];
  for (const raw of titles) {
    const t = raw.toLowerCase();
    if (wordStartsWith(t, q)) best = Math.max(best, 100);
    else if (t.includes(q)) best = Math.max(best, 60);
  }

  // Same shape for keywords — English canonical + non-English translations.
  const allKeywords = [...(target.keywordsEnglish ?? []), ...(target.keywordsOtherLanguages ?? [])];
  for (const kw of allKeywords) {
    const k = kw.toLowerCase();
    if (wordStartsWith(k, q)) best = Math.max(best, 50);
    else if (k.includes(q)) best = Math.max(best, 30);
  }

  return best;
}

function wordStartsWith(haystack: string, q: string): boolean {
  if (haystack.startsWith(q)) return true;
  for (let i = 1; i < haystack.length; i++) {
    const c = haystack[i - 1];
    if (c === ' ' || c === '-' || c === '/' || c === '&') {
      if (haystack.startsWith(q, i)) return true;
    }
  }
  return false;
}
